import React, { useState } from 'react'
import { Modal } from '../../ui/modal'
import { Button } from '../../ui/button'
import { Loader2, Users, AlertTriangle } from 'lucide-react'
import { SURVEY_CONFIG } from '../../../lib/constants'
import { ProcessingOverlay } from '../../ui/processing-overlay'
import { ProcessingToast } from '../../ui/processing-toast'
import { trackEvent, EventCategory } from '../../../lib/analytics'

interface RoleResponse {
  role_name: string
  role_function: string
  role_level: string
  custom_role_name: string | null
  response_count: number
}

interface SurveyResponsesModalProps {
  open: boolean
  onClose: () => void
  onProcess: () => void
  isProcessing: boolean
  processingAttempts: number
  responses: RoleResponse[]
  totalResponses: number
}

export function SurveyResponsesModal({
  open,
  onClose,
  onProcess,
  isProcessing,
  processingAttempts,
  responses,
  totalResponses
}: SurveyResponsesModalProps) {
  const [showProcessingDialog, setShowProcessingDialog] = useState(false)

  const handleProcessClick = () => {
    trackEvent({
      action: 'click',
      category: EventCategory.Survey,
      label: 'process_results'
    })
    setShowProcessingDialog(true)
  }

  const confirmProcessing = () => {
    trackEvent({
      action: 'confirm',
      category: EventCategory.Survey,
      label: 'confirm_process_results'
    })
    setShowProcessingDialog(false)
    onProcess()
  }

  return (
    <>
      {isProcessing && (
        <>
          <ProcessingOverlay />
          <ProcessingToast />
        </>
      )}
      
      <Modal
        open={open && !isProcessing}
        onClose={onClose}
        title="Survey Responses"
        footer={
          <>
            <Button variant="outline" onClick={onClose} disabled={isProcessing}>
              Close
            </Button>
            {totalResponses > 0 && (
              <Button 
                onClick={handleProcessClick}
                disabled={isProcessing || processingAttempts >= SURVEY_CONFIG.PROCESSING.MAX_TOTAL_ATTEMPTS}
              >
                Process Results
              </Button>
            )}
          </>
        }
      >
        <div className="space-y-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
              <Users className="w-6 h-6 text-primary" />
            </div>
            <div>
              <p className="text-lg font-medium">Total Responses</p>
              <p className="text-3xl font-bold">{totalResponses}</p>
            </div>
          </div>

          {totalResponses > 0 ? (
            <div className="border rounded-lg">
              <div className="p-4 border-b bg-muted/50">
                <div className="flex justify-between items-center">
                  <div>
                    <h4 className="font-medium">Role</h4>
                  </div>
                  <div className="text-right">
                    <p className="font-medium">Responses</p>
                  </div>
                </div>
              </div>
              
              <div className="divide-y max-h-[320px] overflow-y-auto">
                {responses.map((response, index) => (
                  <div key={index} className="p-4 hover:bg-muted/50 transition-colors">
                    <div className="flex justify-between items-start">
                      <div>
                        <h4 className="font-medium">
                          {response.custom_role_name || response.role_name}
                        </h4>
                        {!response.custom_role_name && (
                          <p className="text-sm text-muted-foreground">
                            {response.role_function} • {response.role_level}
                          </p>
                        )}
                      </div>
                      <div className="text-right">
                        <p className="font-medium">{response.response_count}</p>
                        <p className="text-sm text-muted-foreground">
                          {response.response_count === 1 ? 'Response' : 'Responses'}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <p className="text-center text-muted-foreground py-8">
              No responses received yet.
            </p>
          )}
        </div>

        <Modal
          open={showProcessingDialog}
          onClose={() => setShowProcessingDialog(false)}
          title="Process Survey Results"
        >
          <div className="space-y-4">
            <div className="flex items-center gap-4 p-4 rounded-lg bg-primary/5 border border-primary/10">
              <div>
                <p className="font-medium">Processing Time</p>
                <p className="text-sm text-muted-foreground">
                  Results processing typically takes 1-2 minutes to complete. During this time, our AI system will analyze all responses
                  and generate comprehensive insights.
                </p>
              </div>
            </div>

            <div className="flex items-center gap-4 p-4 rounded-lg bg-yellow-50 border border-yellow-200 dark:bg-yellow-900/10 dark:border-yellow-900/20">
              <AlertTriangle className="h-5 w-5 text-yellow-600 dark:text-yellow-500 flex-shrink-0" />
              <p className="text-sm text-yellow-800 dark:text-yellow-200">
                <strong>DO NOT CLOSE YOUR BROWSER</strong> - Closing this window will interrupt the analysis and may result in data loss.
              </p>
            </div>
            
            <p>
              You can only process a survey's results {SURVEY_CONFIG.PROCESSING.MAX_TOTAL_ATTEMPTS} times in total, 
              with a {SURVEY_CONFIG.PROCESSING.COOLDOWN_HOURS}-hour waiting period between attempts.
            </p>
            <p>
              You have used {processingAttempts} out of {SURVEY_CONFIG.PROCESSING.MAX_TOTAL_ATTEMPTS} attempts for this survey.
            </p>
            <p>Would you like to process the results now?</p>

            <div className="flex justify-end gap-2 mt-6">
              <Button variant="outline" onClick={() => setShowProcessingDialog(false)}>
                Cancel
              </Button>
              <Button onClick={confirmProcessing}>
                Process Results
              </Button>
            </div>
          </div>
        </Modal>
      </Modal>
    </>
  )
}